import { Drawer, Button, Typography, Box, IconButton, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OperationForm } from './OperationForm';
import { useEffect, useState } from 'react';
import { PatchType } from '../../../types';
import { getOperationTypesForPatchType, formatOperationTypeName } from '../constants/operationTypes';
// import { useDispatch } from 'react-redux';

import { useMenuDataCache } from '../hooks/useMenuDataCache';
import { useOperationForm } from '../hooks/useOperationForm';
import type { PatchOperation, Patch, Store } from '../types/patchTypes';



interface OperationDrawerProps {
  mode?: 'create' | 'edit' | 'remove';
  initialEdit?: PatchOperation | null;
  onClose?: () => void;
  patchType?: PatchType;
  availablePatches?: Patch[];
  selectedStores?: Store[];
  getPatchIdsForType?: (patchType: PatchType) => string[];
  addPendingOp?: (opObj: { minimal: PatchOperation; enriched: PatchOperation }, type?: 'set' | 'remove') => void;
}

export function OperationDrawer({ mode = 'create', initialEdit = null, onClose, patchType = PatchType.PRODUCT_PRICE, availablePatches = [], selectedStores = [], getPatchIdsForType, addPendingOp }: OperationDrawerProps) {
  // Determine mode flags early
  const isEdit = mode === 'edit';
  const isRemove = mode === 'remove';

  // Get all operation types allowed for the current patch type using shared constant
  const uniqueOperationTypes = getOperationTypesForPatchType(patchType);
  // console.log('[OperationDrawer] patchType:', patchType, 'uniqueOperationTypes:', uniqueOperationTypes, 'availablePatches:', availablePatches);

  // Operation type selection
  const [operationType, setOperationType] = useState<string>(uniqueOperationTypes[0] || '');

  // Menu data cache
  const { menu, fetchMenuData } = useMenuDataCache();

  // Operation form state

  const {
    fields,
    handlers
  } = useOperationForm({
    mode,
    operationTypes: uniqueOperationTypes,
    availablePatches,
    addPendingOp,
    menu: menu || { products: [], bundles: [] }, // Provide fallback for null menu
  });

  // On mount, fetch menu data (will only load once due to cache logic)
  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // When patch type changes, update operation type to first available
  useEffect(() => {
    if (uniqueOperationTypes.length > 0) {
      setOperationType(uniqueOperationTypes[0]);
      handlers.setOperationType(uniqueOperationTypes[0]);
    }
  }, [patchType]);

  // When operation type changes, update form state
  useEffect(() => {
    if (operationType) {
      handlers.setOperationType(operationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationType]);

  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  const handleSave = () => {
    // Always enabled, just call the form's save handler
    handlers.save();
    handleClose();
  };

  return (
    <>
      {isEdit && (
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          color="primary"
        >
          Edit Operation
        </Button>
      )}

      <Drawer anchor="right" open={open || isEdit} onClose={handleClose}>
        <Box p={2} width={600} data-testid="operationWindow">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              {isEdit ? 'Edit Operation' : isRemove ? 'Remove Operation' : 'Add Operation'}
            </Typography>
            <IconButton 
              onClick={handleClose} 
              data-testid="closeOperationDrawerButton"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Patch selection dropdown removed: operations are based on patch type only */}

          {/* Operation type select */}
          {uniqueOperationTypes.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="operation-type-select-label">Operation Type</InputLabel>
              <Select
                labelId="operation-type-select-label"
                data-testid="operation-type-select"
                value={operationType}
                label="Operation Type"
                onChange={(e) => setOperationType(e.target.value)}
              >
                {uniqueOperationTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {formatOperationTypeName(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Operation form for all modes */}
          {operationType && (
            <Box mt={2}>
              <OperationForm
                fields={fields}
                handlers={handlers}
                mode={mode}
              />
            </Box>
          )}

          {/* Action buttons */}
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              data-testid="cancelOperationButton"
              onClick={handleClose}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              data-testid="saveOperationButton"
              onClick={handleSave}
              color={isRemove ? "error" : "primary"}
            >
              {isEdit ? 'Update' : isRemove ? 'Remove' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
